import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  Check,
  Close,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { format, isValid } from 'date-fns';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { buildUploadUrl, fetchFileBlobUrl, fetchDocumentBlobUrl } from '../../utils/fileAccess';
import StatusLabel from '../../components/Status/StatusLabel';
import { countersignedNdaOf } from '../../components/Documents/ndaCopies';

const ApplicationStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAccordion, setExpandedAccordion] = useState('basicInformation');
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);
  const [fileViewerName, setFileViewerName] = useState('');

  useEffect(() => {
    if (id === 'status') {
      resolveStatusRedirect();
    } else if (id) {
      fetchSupplierApplication();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const resolveStatusRedirect = async () => {
    try {
      const response = await api.get('/suppliers/my-applications');
      const apps = response.data.data || [];
      if (apps.length === 1) {
        navigate(`/application/${apps[0]._id}`, { replace: true });
      } else if (apps.length > 1) {
        // Most recent submitted/active application first
        const sorted = [...apps].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        navigate(`/application/${sorted[0]._id}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      navigate('/dashboard', { replace: true });
    }
  };

  const fetchSupplierApplication = async () => {
    try {
      const response = await api.get(`/suppliers/${id}`);
      setSupplier(response.data.data);
    } catch (error) {
      toast.error('Failed to load application');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isValid(date)) {
        return format(date, 'do MMMM, yyyy');
      }
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      draft: 'Draft',
      submitted: 'Pending Procurement Approval',
      pending_procurement: 'Pending Procurement Approval',
      pending_legal: 'Pending Legal Approval',
      under_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
      more_info_required: 'Requested More Info',
      pending_contract_upload: 'Prequalified and Pending Contract Upload',
      completed: 'Completed',
    };
    return statusMap[status] || status;
  };

  const getApprovalStep = () => {
    if (!supplier) return 0;
    if (supplier.status === 'rejected') return 0;

    const statusMap = {
      draft: 0,
      submitted: 1,
      pending_procurement: 1,
      more_info_required: 1,
      pending_legal: 2,
      under_review: 2,
      pending_contract_upload: 3,
      approved: 3,
      completed: 3,
      active: 3,
    };

    // currentApprovalStage is set during every approval transition and is
    // often more up-to-date than status for edge cases
    const stageMap = {
      procurement: 1,
      legal: 2,
      contract_upload: 3,
      completed: 3,
    };

    const fromStatus = statusMap[supplier.status] ?? -1;
    const fromStage = stageMap[supplier.currentApprovalStage] ?? -1;

    // Take the furthest step from either signal so we never show a step
    // lower than what the workflow has already reached
    return Math.max(0, fromStatus, fromStage);
  };

  const approvalSteps = [
    { number: 1, title: 'Application Created', description: 'Your application has been submitted' },
    { number: 2, title: 'Procurement Review', description: 'Under review by procurement team' },
    { number: 3, title: 'Legal Review', description: 'Under review by legal team' },
    { number: 4, title: 'Approved', description: 'Application has been approved' },
  ];

  const activeStep = getApprovalStep();

  // A returned application is the one state on this page the supplier has to
  // act on. The notification that brings them here links to this page, so the
  // request and the way back into the form both have to be on it — the
  // approval history accordion further down is a record, not a prompt.
  const isReturned = supplier?.status === 'more_info_required';
  const infoRequest = isReturned
    ? [...(supplier.approvalHistory || [])].filter((h) => h.action === 'requested_info').pop()
    : null;

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!supplier) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            No Application Found
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Application not found.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  // Map supplier data to formData-like structure for display
  const formData = {
    supplierName: supplier.supplierName || '',
    legalNature: supplier.legalNature || '',
    entityType: supplier.entityType || '',
    companyRegistrationNumber: supplier.companyRegistrationNumber || '',
    companyEmail: supplier.companyEmail || '',
    companyWebsite: supplier.companyWebsite || '',
    registeredCountry: supplier.registeredCountry || '',
    physicalAddress: supplier.physicalAddress || '',
    companyPhysicalAddress: supplier.companyPhysicalAddress || {},
    contactFullName: supplier.authorizedPerson?.name || '',
    contactRelationship: supplier.authorizedPerson?.relationship || '',
    contactIdPassport: supplier.authorizedPerson?.idPassportNumber || '',
    contactPhone: supplier.authorizedPerson?.phone || '',
    contactEmail: supplier.authorizedPerson?.email || '',
    bankName: supplier.bankName || '',
    accountNumber: supplier.accountNumber || supplier.bankAccountNumber || '',
    branch: supplier.branch || supplier.bankBranch || '',
    creditPeriod: supplier.creditPeriod || '',
    serviceTypes: supplier.serviceTypes || supplier.serviceType || '',
    serviceTypesOther: supplier.serviceTypesOther || '',
    businessPermit: supplier.businessPermit || null,
    certificateOfIncorporation: supplier.certificateOfIncorporation || null,
    kraPinCertificate: supplier.kraPinCertificate || null,
    etimsProof: supplier.etimsProof || null,
    financialStatements: supplier.financialStatements || null,
    cr12: supplier.cr12 || null,
    companyProfile: supplier.companyProfile || null,
    bankReferenceLetter: supplier.bankReferenceLetter || null,
    directorsIds: supplier.directorsIds || [],
    // Partnership
    partnershipDeed: supplier.partnershipDeed || null,
    partnersPinCertificate: supplier.partnersPinCertificate || null,
    partnersTaxCompliance: supplier.partnersTaxCompliance || null,
    partnerIds: supplier.partnerIds || [],
    // Foreign Company
    shareCertificate: supplier.shareCertificate || null,
    registryExtract: supplier.registryExtract || null,
    taxComplianceCertificate: supplier.taxComplianceCertificate || null,
    directorsNationalIds: supplier.directorsNationalIds || [],
    directorsPassports: supplier.directorsPassports || [],
    // Individual / Sole Proprietor
    nationalId: supplier.nationalId || null,
    passportDocument: supplier.passportDocument || null,
    workPermit: supplier.workPermit || null,
    policeClearance: supplier.policeClearance || null,
    resume: supplier.resume || null,
    // Trust
    trustDeed: supplier.trustDeed || null,
    founderPin: supplier.founderPin || null,
    foundersIds: supplier.foundersIds || [],
    beneficiariesIds: supplier.beneficiariesIds || [],
    practicingCertificates: supplier.practicingCertificates || [],
    keyMembersResumes: supplier.keyMembersResumes || [],
    sourceOfWealth: supplier.sourceOfWealth || '',
    sourceOfWealthOther: supplier.sourceOfWealthOther || '',
    declarantFullName: supplier.declarantFullName || '',
    declarantCapacity: supplier.declarantCapacity || '',
    declarantIdPassport: supplier.declarantIdPassport || '',
    declarationDate: supplier.declarationDate || '',
    declarationSignatureFile: supplier.declarationSignatureFile || null,
  };

  const handleViewFile = async (file, fileName = '') => {
    if (!file) return;

    let fileUrl = null;
    let displayName = fileName || 'File';

    if (file instanceof File) {
      fileUrl = URL.createObjectURL(file);
      displayName = file.name;
    } else if (file && typeof file === 'object' && file._id) {
      displayName = fileName || file.originalName || file.fileName || 'File';
      try {
        fileUrl = await fetchDocumentBlobUrl(file._id);
      } catch (error) {
        // Fallback: try serving directly from static uploads path
        if (file.fileName) {
          const supplierId = file.supplier?._id || file.supplier || id;
          const directUrl = buildUploadUrl(file.fileName, supplierId);
          try {
            fileUrl = await fetchFileBlobUrl(directUrl);
          } catch {
            toast.error('Document is not available. Please contact the administrator to re-upload it.');
            return;
          }
        } else {
          toast.error('Document is not available. Please contact the administrator to re-upload it.');
          return;
        }
      }
      setFileViewerUrl(fileUrl);
      setFileViewerName(displayName);
      setFileViewerOpen(true);
      return;
    } else if (typeof file === 'string') {
      if (file.startsWith('http://') || file.startsWith('https://')) {
        // A stored absolute URL pointing at our own old /uploads path still
        // names a supplier directory and a file; resolve it to the
        // authenticated /files route rather than fetching it directly.
        fileUrl = buildUploadUrl(file, supplier?._id || id) || file;
      } else {
        fileUrl = buildUploadUrl(file, supplier?._id || id);
      }
      displayName = fileName || file.split('/').pop() || 'File';
    }

    if (fileUrl) {
      if (!fileUrl.startsWith('blob:')) {
        try {
          fileUrl = await fetchFileBlobUrl(fileUrl);
        } catch (error) {
          toast.error(error.message || 'Failed to open file');
          return;
        }
      }
      setFileViewerUrl(fileUrl);
      setFileViewerName(displayName);
      setFileViewerOpen(true);
    }
  };

  const handleCloseFileViewer = () => {
    if (fileViewerUrl && fileViewerUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerOpen(false);
    setFileViewerUrl(null);
    setFileViewerName('');
  };

  const renderDocumentCard = (fileName, documentName) => {
    if (!fileName) return null;
    const isDocObj = fileName && typeof fileName === 'object' && fileName._id;
    const displayName = isDocObj
      ? (fileName.originalName || fileName.fileName || 'Document')
      : (typeof fileName === 'string' ? fileName.split('/').pop() : 'File selected');
    const fileExtension = displayName.split('.').pop()?.toUpperCase() || 'PDF';

    return (
      <Box
        key={documentName}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          mb: 1,
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          backgroundColor: '#fff',
          '&:hover': {
            borderColor: '#d1d5db',
            backgroundColor: '#f9fafb'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            component="img"
            src="/images/File.svg"
            alt="File icon"
            sx={{ width: 24, height: 24, color: '#6b7280' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 0.25 }}>
              {documentName}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
              {fileExtension}{isDocObj && fileName.fileSize ? ` • ${(fileName.fileSize / (1024 * 1024)).toFixed(1)} MB` : ''}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => handleViewFile(fileName, documentName)}
          sx={{
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#374151'
            }
          }}
        >
          <Box
            component="img"
            src="/images/eye.svg"
            alt="View icon"
            sx={{ width: 20, height: 20 }}
          />
        </IconButton>
      </Box>
    );
  };

  const renderMultipleDocumentCards = (files, documentName) => {
    if (!files || files.length === 0) return null;
    return files.map((file, index) => {
      const displayName = files.length > 1
        ? `${documentName} ${index + 1}`
        : (typeof file === 'string' ? file : documentName);
      const fileExtension = (typeof file === 'string' ? file : '').split('.').pop()?.toUpperCase() || 'PDF';

      return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            mb: 1,
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: '#d1d5db',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box
              component="img"
              src="/images/File.svg"
              alt="File icon"
              sx={{ width: 24, height: 24, color: '#6b7280' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 0.25 }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                {fileExtension} • 2.3 MB
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleViewFile(file, displayName)}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#374151'
              }
            }}
          >
            <Box
              component="img"
              src="/images/eye.svg"
              alt="View icon"
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        </Box>
      );
    });
  };

  return (
    <Box sx={{ backgroundColor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Container maxWidth="lg" sx={{ pt: 3, pb: 4, flex: 1 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{
            color: '#6b7280',
            textTransform: 'none',
            fontSize: '14px',
            mb: 3,
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#374151'
            }
          }}
        >
          Back to Dashboard
        </Button>

        {/* Returned for more information — what was asked, and the way back in */}
        {isReturned && (
          <Box
            sx={{
              mb: 3,
              p: { xs: 1.75, sm: 2.25 },
              border: '1px solid #fde68a',
              borderRadius: '8px',
              backgroundColor: '#fffbeb',
              display: { xs: 'block', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ mb: { xs: 1.5, sm: 0 } }}>
              <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#92400e', mb: 0.25 }}>
                {infoRequest?.approver?.department
                  ? `${infoRequest.approver.department} needs more information`
                  : 'More information needed'}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#b45309', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {infoRequest?.comments || 'A reviewer has asked you to update your application.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate(`/application/${supplier._id}/edit`)}
              sx={{
                backgroundColor: theme.palette.green.main,
                color: '#fff',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                px: 2.5,
                py: 1,
                borderRadius: '6px',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { backgroundColor: theme.palette.green.hover, boxShadow: 'none' },
              }}
            >
              Update application
            </Button>
          </Box>
        )}

        {/* Approval Workflow Steps */}
        <Box sx={{ mb: 4 }}>
          {/* Mobile View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', alignItems: 'center', mb: 3 }}>
            {approvalSteps.map((step, index) => (
              <Box key={step.number} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: index <= activeStep ? theme.palette.green.main : 'transparent',
                    color: index <= activeStep ? '#fff' : theme.palette.green.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {index < activeStep ? <Check sx={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }} /> : step.number}
                </Box>
                {index < approvalSteps.length - 1 && (
                  <Box
                    sx={{
                      width: 40,
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      mx: 2
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Desktop View */}
          <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {approvalSteps.map((step, index) => (
              <Grid item xs={3} key={step.number}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: index <= activeStep ? theme.palette.green.main : 'transparent',
                      color: index <= activeStep ? '#fff' : theme.palette.green.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '18px',
                      margin: '0 auto 8px',
                    }}
                  >
                    {index < activeStep ? <Check sx={{ fontSize: '20px', fontWeight: 'bold', color: '#fff' }} /> : step.number}
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827', mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Details - Similar to Step 4 */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          {/* Basic Information Accordion */}
          <Accordion
            expanded={expandedAccordion === 'basicInformation'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'basicInformation' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Basic Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Company Name
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.supplierName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Legal Nature
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.legalNature || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Entity Type
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.entityType || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Company Registration Number
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.companyRegistrationNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Company Email
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.companyEmail || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Company Website
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.companyWebsite || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Registered Country
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.registeredCountry || '-'}
                  </Typography>
                </Grid>
                {/* Physical Address */}
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Physical Address
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.companyPhysicalAddress?.street || formData.physicalAddress || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Contact Details Accordion */}
          <Accordion
            expanded={expandedAccordion === 'contactDetails'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'contactDetails' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Contact Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Full Name
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.contactFullName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Relationship
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.contactRelationship || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    ID/Passport Number
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.contactIdPassport || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Phone
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.contactPhone || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Email
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.contactEmail || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Payment Details Accordion */}
          <Accordion
            expanded={expandedAccordion === 'paymentDetails'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'paymentDetails' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Payment Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Bank Name
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.bankName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Account Number
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.accountNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Branch
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.branch || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Applicable Credit Period
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.creditPeriod ? `${formData.creditPeriod} Days` : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Entity Details Accordion */}
          <Accordion
            expanded={expandedAccordion === 'entityDetails'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'entityDetails' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Entity Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {[
                  { field: 'businessPermit',             label: 'Business Permit / Trading Licence',              type: 'single' },
                  { field: 'bankReferenceLetter',         label: 'Bank Reference Letter',                         type: 'single' },
                  { field: 'certificateOfIncorporation',  label: 'Certificate of Incorporation or Registration',  type: 'single' },
                  { field: 'kraPinCertificate',           label: 'PIN Certificate',                               type: 'single' },
                  { field: 'etimsProof',                  label: 'Proof of Registration on e-TIMS',               type: 'single' },
                  { field: 'financialStatements',         label: 'Annual Audited Financial Statements',           type: 'single' },
                  { field: 'cr12',                        label: 'Valid CR12',                                    type: 'single' },
                  { field: 'companyProfile',              label: 'Company Profile',                               type: 'single' },
                  { field: 'directorsIds',                label: "Directors' IDs / Passports",                    type: 'multi'  },
                  { field: 'partnershipDeed',             label: 'Partnership Deed',                              type: 'single' },
                  { field: 'partnersPinCertificate',      label: "Partners' PIN Certificate",                     type: 'single' },
                  { field: 'partnersTaxCompliance',       label: "Partners' Tax Compliance Certificate",          type: 'single' },
                  { field: 'partnerIds',                  label: "Partners' IDs / Passports",                     type: 'multi'  },
                  { field: 'shareCertificate',            label: 'Valid Share Certificate',                       type: 'single' },
                  { field: 'registryExtract',             label: 'Valid Registry Extract',                        type: 'single' },
                  { field: 'taxComplianceCertificate',    label: 'Valid Tax Compliance Certificate',              type: 'single' },
                  { field: 'directorsNationalIds',        label: "Directors' National IDs",                       type: 'multi'  },
                  { field: 'directorsPassports',          label: "Directors' Passports",                          type: 'multi'  },
                  { field: 'nationalId',                  label: 'National Identification Card',                  type: 'single' },
                  { field: 'passportDocument',            label: 'Passport',                                      type: 'single' },
                  { field: 'workPermit',                  label: 'Work Permit (for foreigners)',                  type: 'single' },
                  { field: 'policeClearance',             label: 'Police Clearance Certificate',                  type: 'single' },
                  { field: 'resume',                      label: 'Resume (Curriculum Vitae)',                     type: 'single' },
                  { field: 'trustDeed',                   label: 'Trust Deed',                                    type: 'single' },
                  { field: 'founderPin',                  label: "Founders' PIN Certificate",                     type: 'single' },
                  { field: 'foundersIds',                 label: "Founders' IDs / Passports",                     type: 'multi'  },
                  { field: 'beneficiariesIds',            label: "Beneficiaries' IDs / Passports",                type: 'multi'  },
                ].map((doc) => {
                  if (doc.type === 'multi') {
                    const files = formData[doc.field];
                    return files?.length > 0 ? renderMultipleDocumentCards(files, doc.label) : null;
                  }
                  return formData[doc.field] ? renderDocumentCard(formData[doc.field], doc.label) : null;
                })}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Service Details Accordion */}
          <Accordion
            expanded={expandedAccordion === 'serviceDetails'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'serviceDetails' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Service Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {formData.serviceTypes && (
                  <Typography sx={{ mb: 2, fontSize: '14px', color: '#374151' }}>
                    <span style={{ color: '#6b7280', marginRight: 8 }}>Type of Service:</span>
                    <strong>
                      {formData.serviceTypes === 'Other'
                        ? (formData.serviceTypesOther || 'Other')
                        : formData.serviceTypes}
                    </strong>
                  </Typography>
                )}
                {renderMultipleDocumentCards(formData.practicingCertificates, 'Practicing Certificate')}
                {renderMultipleDocumentCards(formData.keyMembersResumes, 'Resume')}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Source of Funds Declaration Accordion */}
          <Accordion
            expanded={expandedAccordion === 'sourceOfFunds'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'sourceOfFunds' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Source of Funds Declaration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Source of wealth/Funds
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.sourceOfWealth === 'Other'
                      ? (formData.sourceOfWealthOther || 'Other')
                      : (formData.sourceOfWealth || '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Full Name of Declarant
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.declarantFullName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Capacity
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.declarantCapacity || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    ID/Passport Number
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.declarantIdPassport || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Date
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formatDate(formData.declarationDate)}
                  </Typography>
                </Grid>
                {formData.declarationSignatureFile && (
                  <Grid item xs={12}>
                    {renderDocumentCard(formData.declarationSignatureFile, 'Signature File')}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Personal Information Processing Consent Accordion */}
          <Accordion
            expanded={expandedAccordion === 'consent'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'consent' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Personal Information Processing Consent
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Full Name of Declarant
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formData.declarantFullName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                    Date
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    {formatDate(formData.declarationDate)}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Countersigned NDA: the fully executed copy, once Legal has uploaded it */}
          {countersignedNdaOf(supplier) && (
            <Accordion
              expanded={expandedAccordion === 'countersignedNda'}
              onChange={(event, isExpanded) => {
                setExpandedAccordion(isExpanded ? 'countersignedNda' : '');
              }}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderBottom: '1px solid #e5e7eb',
                borderRadius: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    my: 2
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                  Non-Disclosure Agreement
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderDocumentCard(countersignedNdaOf(supplier), 'NDA – countersigned by Betika')}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Signed Contract Accordion */}
          {(supplier.status === 'completed' || supplier.contract?.status === 'terminated') && supplier.contract && (supplier.contract.signedContract || supplier.contract.status === 'terminated') && (
            <Accordion
              expanded={expandedAccordion === 'contract'}
              onChange={(event, isExpanded) => {
                setExpandedAccordion(isExpanded ? 'contract' : '');
              }}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderBottom: '1px solid #e5e7eb',
                borderRadius: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    my: 2
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Contract
                  </Typography>
                  {supplier.contract?.status === 'terminated' && (
                    <Box sx={{ px: 1, py: 0.25, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase' }}>
                        Terminated
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Grid container spacing={2.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Contract Number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {supplier.contract.contractNumber || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Start Date
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formatDate(supplier.contract.startDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Expiry Date
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formatDate(supplier.contract.endDate)}
                      </Typography>
                    </Grid>
                    {supplier.contract?.status === 'terminated' && supplier.contract.terminatedAt && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                          Terminated On
                        </Typography>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#dc2626' }}>
                          {formatDate(supplier.contract.terminatedAt)}
                        </Typography>
                      </Grid>
                    )}
                    {supplier.contract?.terminationReason && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                          Termination Reason
                        </Typography>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                          {supplier.contract.terminationReason}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  {supplier.contract.signedContract && renderDocumentCard(
                    supplier.contract.signedContract,
                    'Signed Contract - ' + (supplier.contract.contractNumber || '')
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Approval History Accordion */}
          <Accordion
            expanded={expandedAccordion === 'approvalHistory'}
            onChange={(event, isExpanded) => {
              setExpandedAccordion(isExpanded ? 'approvalHistory' : '');
            }}
            sx={{
              boxShadow: 'none',
              border: 'none',
              borderRadius: 0,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  my: 2
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                Approval History
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {supplier.approvalHistory && supplier.approvalHistory.length > 0 ? (
                <Box>
                  {supplier.approvalHistory.map((history, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                          {history.action || 'Status Update'}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                          {formatDate(history.timestamp || history.date)}
                        </Typography>
                      </Box>
                      {history.approver?.department && (
                        <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 1 }}>
                          Department: {history.approver.department}
                        </Typography>
                      )}
                      {history.comments && (
                        <Typography sx={{ fontSize: '13px', color: '#374151', mt: 1 }}>
                          {history.comments}
                        </Typography>
                      )}
                      {history.status && (
                        <StatusLabel kind="application" status={history.status} label={getStatusLabel(history.status)} sx={{ mt: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', py: 2 }}>
                  No approval history available yet
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Container>

      {/* File Viewer Modal */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', borderRadius: '8px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>{fileViewerName}</Typography>
          <IconButton onClick={handleCloseFileViewer} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#f5f5f5', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fileViewerUrl && (
            fileViewerName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img
                src={fileViewerUrl}
                alt={fileViewerName}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                src={fileViewerUrl}
                title={fileViewerName}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          <Button variant="contained" onClick={() => window.open(fileViewerUrl, '_blank')}>
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationStatus;
